import React, {useMemo} from 'react';
import {LocalizationProvider} from '@mui/x-date-pickers-pro';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {assert} from 'assert-ts';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {useParams} from 'react-router';
import type {UpdateMetadataParamsContextType} from 'types';
import {CodeListsGate} from 'services/codelists';
import {useMetadataCodelistIds} from 'services/metadata';
import {ThesaurusesGate} from 'services/thesaurus';
import {useAllThesaurusIds} from 'services/thesaurus/hooks/useAllThesaurusIds';
import {MockContext} from 'services/utils/contexts';
import {
  useLinkedValueSearchSceneExtensionCore,
  useMultiThesaurusEditSceneExtensionCore,
  useThesaurusEditSceneExtensionCore,
} from 'sceneExtensions';
import {
  SceneExtensionsContext,
  useSceneExtensionsContextProviderValue,
} from 'sceneExtensions/contexts';
import {WorkScene} from './WorkScene';
import {MetadataEditStateGate} from './components';
import {
  MetadataOperationsContextProvider,
  WorkToggleContext,
  useWorkToggleContextProviderValue,
} from './contexts';
import {ExternalSuggestionsContextProvider} from './contexts/ExternalSuggestionsContext';
import {useLinkedValueSearchPageConfig} from './hooks/useLinkedValueSearchPageConfig';

dayjs.extend(utc);

export const UpdateMetadataScene: React.FC = () => {
  const params = useParams();

  const workId = assert(
    params['workId'],
    'UpdateMetadataScene: workId parameter expected',
  );

  const thesaurusIds = useAllThesaurusIds();
  const paramsContext: UpdateMetadataParamsContextType = {
    workId,
    manifestationDesignator: undefined,
    mock: false,
  };

  const codelistIds = useMetadataCodelistIds();

  const linkedValueSearchPageConfig = useLinkedValueSearchPageConfig({
    id: paramsContext.workId,
  });

  const thesaurusEditExtension = useThesaurusEditSceneExtensionCore();
  const multiThesaurusEditExtension = useMultiThesaurusEditSceneExtensionCore();
  const linkedValueSearchExtension = useLinkedValueSearchSceneExtensionCore(
    {
      canToggleFullWidth: true,
    },
    linkedValueSearchPageConfig,
  );
  const extensions = useMemo(
    () => [
      thesaurusEditExtension,
      multiThesaurusEditExtension,
      linkedValueSearchExtension,
    ],
    [
      linkedValueSearchExtension,
      multiThesaurusEditExtension,
      thesaurusEditExtension,
    ],
  );

  const sceneExtensionContext =
    useSceneExtensionsContextProviderValue(extensions);

  const workToggleValue = useWorkToggleContextProviderValue();

  // Load codelists, thesauruses and metadata, then initialize MetadataEditState
  return (
    <MockContext.Provider value={paramsContext.mock}>
      <CodeListsGate codeLists={codelistIds}>
        <ThesaurusesGate thesauruses={thesaurusIds}>
          <MetadataEditStateGate workId={workId}>
            <ExternalSuggestionsContextProvider>
              <MetadataOperationsContextProvider>
                <SceneExtensionsContext.Provider value={sceneExtensionContext}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <WorkToggleContext.Provider value={workToggleValue}>
                      <WorkScene workId={workId} />
                    </WorkToggleContext.Provider>
                  </LocalizationProvider>
                </SceneExtensionsContext.Provider>
              </MetadataOperationsContextProvider>
            </ExternalSuggestionsContextProvider>
          </MetadataEditStateGate>
        </ThesaurusesGate>
      </CodeListsGate>
    </MockContext.Provider>
  );
};
